/**
 * Weglot search helpers.
 * -----------------------------------------------------------------------------
 * Translates customer search terms back to the store's source language before
 * Algolia searches its source-language indexes.
 */

const SOURCE_LANGUAGE = 'en'
const WEGLOT_WORD_TYPE_TEXT = 1
const WEGLOT_READY_TIMEOUT = 2000
const TRANSLATION_TIMEOUT = 2000
const WEGLOT_READY_INTERVAL = 50
const localePrefixPattern = /^[a-z]{2}(?:-[a-z]{2})?$/i
const preferredSearchTermReplacements = [
  {
    pattern: /\bpants\b/gi,
    replacement: 'trousers',
  },
]
const preferredSourceSearchTerms = {
  pantalon: 'trousers',
  pantalón: 'trousers',
  pantalones: 'trousers',
}
const translationCache = new Map()

function getUrlLanguage() {
  const pathSegment = window.location.pathname.split('/').filter(Boolean)[0]

  if (!localePrefixPattern.test(pathSegment)) {
    return ''
  }

  return pathSegment.split('-')[0].toLowerCase()
}

function getSearchLanguage() {
  return (
    window.Weglot?.getCurrentLang?.() ||
    getUrlLanguage() ||
    document.documentElement.getAttribute('lang') ||
    window.cnvs?.store?.language ||
    SOURCE_LANGUAGE
  ).toLowerCase()
}

function debugWeglotSearch(message, data = {}) {
  console.log(`[Weglot search] ${message}`, {
    searchLanguage: getSearchLanguage(),
    shopifyLanguage: window.cnvs?.store?.language,
    shopifyLanguagePrimary: window.cnvs?.store?.languagePrimary,
    hasWeglot: !!window.Weglot,
    hasSearch: !!window.Weglot?.search,
    hasTranslate: !!window.Weglot?.translate,
    ...data,
  })
}

function isTranslatedLanguage() {
  return getSearchLanguage() !== SOURCE_LANGUAGE
}

function waitForWeglot() {
  if (window.Weglot?.search || window.Weglot?.translate) {
    debugWeglotSearch('Weglot ready immediately')
    return Promise.resolve(window.Weglot)
  }

  debugWeglotSearch('Waiting for Weglot')

  return new Promise((resolve) => {
    const startedAt = Date.now()
    const interval = window.setInterval(() => {
      if (window.Weglot?.search || window.Weglot?.translate) {
        window.clearInterval(interval)
        debugWeglotSearch('Weglot became ready')
        resolve(window.Weglot)
        return
      }

      if (Date.now() - startedAt >= WEGLOT_READY_TIMEOUT) {
        window.clearInterval(interval)
        debugWeglotSearch('Weglot ready timeout')
        resolve(null)
      }
    }, WEGLOT_READY_INTERVAL)
  })
}

function getTranslatedValue(response, query) {
  const firstResult = Array.isArray(response) ? response[0] : response

  if (typeof firstResult === 'string') {
    return firstResult
  }

  if (firstResult?.w) {
    return firstResult.w
  }

  if (firstResult?.word) {
    return firstResult.word
  }

  if (firstResult?.translation) {
    return firstResult.translation
  }

  if (firstResult?.words?.[0]?.w) {
    return firstResult.words[0].w
  }

  if (firstResult && typeof firstResult === 'object') {
    const firstValue = Object.values(firstResult).find((value) => typeof value === 'string')

    if (firstValue) {
      return firstValue
    }
  }

  return query
}

function applyPreferredSearchTerms(query) {
  const normalisedQuery = query?.trim().toLowerCase()

  if (preferredSourceSearchTerms[normalisedQuery]) {
    return preferredSourceSearchTerms[normalisedQuery]
  }

  return preferredSearchTermReplacements.reduce((preferredQuery, replacement) => (
    preferredQuery.replace(replacement.pattern, replacement.replacement)
  ), query)
}

export function translateSearchQuery(query) {
  const normalisedQuery = query?.trim()

  debugWeglotSearch('translateSearchQuery called', {
    query,
    normalisedQuery,
    isTranslatedLanguage: isTranslatedLanguage(),
  })

  if (!normalisedQuery || !isTranslatedLanguage()) {
    debugWeglotSearch('Skipping translation', {
      query,
      reason: !normalisedQuery ? 'empty query' : 'primary/source language',
    })
    return Promise.resolve(query)
  }

  const cacheKey = `${getSearchLanguage()}:${normalisedQuery}`

  if (translationCache.has(cacheKey)) {
    debugWeglotSearch('Using cached translation', {
      originalQuery: normalisedQuery,
      translatedQuery: translationCache.get(cacheKey),
    })
    return Promise.resolve(translationCache.get(cacheKey))
  }

  return new Promise(async (resolve) => {
    const weglot = await waitForWeglot()

    if (!weglot) {
      debugWeglotSearch('Sending original query because Weglot is unavailable', {
        originalQuery: normalisedQuery,
      })
      resolve(normalisedQuery)
      return
    }

    const timeout = window.setTimeout(() => {
      debugWeglotSearch('Translation timeout, sending original query', {
        originalQuery: normalisedQuery,
      })
      resolve(normalisedQuery)
    }, TRANSLATION_TIMEOUT)

    try {
      if (weglot.search) {
        debugWeglotSearch('Calling Weglot.search', {
          originalQuery: normalisedQuery,
        })

        weglot.search(normalisedQuery, (translatedQuery) => {
          window.clearTimeout(timeout)
          const preferredQuery = applyPreferredSearchTerms(translatedQuery || normalisedQuery)
          debugWeglotSearch('Weglot.search response', {
            originalQuery: normalisedQuery,
            translatedQuery,
            preferredQuery,
          })

          if (preferredQuery && preferredQuery !== normalisedQuery) {
            translationCache.set(cacheKey, preferredQuery)
          }

          resolve(preferredQuery || normalisedQuery)
        })
        return
      }

      debugWeglotSearch('Calling Weglot.translate fallback', {
        originalQuery: normalisedQuery,
      })

      weglot.translate(
        {
          words: [
            {
              t: WEGLOT_WORD_TYPE_TEXT,
              w: normalisedQuery,
            },
          ],
          languageTo: SOURCE_LANGUAGE,
        },
        (response) => {
          window.clearTimeout(timeout)
          const translatedQuery = getTranslatedValue(response, normalisedQuery)
          const preferredQuery = applyPreferredSearchTerms(translatedQuery)
          debugWeglotSearch('Translated query', {
            originalQuery: normalisedQuery,
            translatedQuery,
            preferredQuery,
            response,
          })

          if (preferredQuery && preferredQuery !== normalisedQuery) {
            translationCache.set(cacheKey, preferredQuery)
          }

          resolve(preferredQuery)
        },
      )
    } catch (error) {
      window.clearTimeout(timeout)
      debugWeglotSearch('Translation error, sending original query', {
        originalQuery: normalisedQuery,
        error,
      })
      resolve(normalisedQuery)
    }
  })
}

export function createTranslatedSearchClient(searchClient) {
  return {
    ...searchClient,
    async search(requests) {
      debugWeglotSearch('Algolia search intercepted', {
        requests: requests.map((request) => ({
          indexName: request.indexName,
          query: request?.params?.query,
        })),
      })

      const queryTranslations = new Map()

      await Promise.all(requests.map(async (request) => {
        const query = request?.params?.query

        if (!query || queryTranslations.has(query)) {
          debugWeglotSearch('Skipping request query translation', {
            query,
            reason: !query ? 'empty query' : 'already translated in batch',
          })
          return
        }

        queryTranslations.set(query, await translateSearchQuery(query))
      }))

      const translatedRequests = requests.map((request) => {
        const query = request?.params?.query

        if (!query || !queryTranslations.has(query)) {
          return request
        }

        return {
          ...request,
          params: {
            ...request.params,
            query: queryTranslations.get(query),
          },
        }
      })

      debugWeglotSearch('Sending Algolia requests', {
        requests: translatedRequests.map((request) => ({
          indexName: request.indexName,
          query: request?.params?.query,
        })),
      })

      return searchClient.search(translatedRequests)
    },
  }
}
